// --- SaniQ ---
import {findInclude} from "api";
import {
  DeserializedRecord,
  deserializeItem,
  deserializeManyRelationship,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIManyRelationship,
  JSONAPIRecord,
  JSONAPISingleRelationship
} from "./Helpers";
import {MeasurementKey} from "vendor/qurasoft/definitions/types/measurements";
import {Classification as EcgClassification} from "./patient/measurement/ECG";
import {deserializeNotificationRecipient, NotificationRecipient} from "./trigger/NotificationRecipient";
import {Survey} from "./Survey";
import {PatientSurvey} from "./patient/Survey";
import {PatientTriggerNotificationGroup} from "./patient/trigger/notification/Group";
import {PatientTriggerNotificationGroupReceipient} from "./patient/trigger/notification/group/Receipient";


type PatientTriggerPriorityType = "normal" | "important" | "critical";
type SubjectTriggers = MeasurementTrigger | MeasurementEcgTrigger | SurveyTrigger |
  SurveyTriggerTemplate | MeasurementEcgTriggerTemplate | MeasurementTriggerTemplate;
type ThresholdType =
  | "more_than"
  | "less_than"
  | "equal_to"
  | "not_equal_to"
  | "more_than_or_equal"
  | "less_than_or_equal"
  | "between"
  | "outside"
  | "adherence"
  | "relative_deviation_period"
  | "relative_deviation_last";

interface PatientTrigger extends DeserializedRecord<"trigger"> {
  adherenceIntensive: boolean;
  adherenceWarningInterval: number;
  color?: string;
  createTaskEnabled?: boolean;
  description?: string;
  patientMessage?: string;
  patientNotify: boolean;
  patientNotifyAdherence: boolean;
  priority: PatientTriggerPriorityType;
  /**
   * When set, adherence alarms are only triggered after a first measurement or survey is recorded
   */
  silentBeginning: boolean;
  threshold: number;
  periodInDays: number;
  thresholdType: ThresholdType;
  notificationRecipients: NotificationRecipient[];
  patientTriggerNotificationGroups: PatientTriggerNotificationGroup[];
  subjectType: string;
  subject: MeasurementEcgTrigger | MeasurementEcgTriggerTemplate |
    MeasurementTrigger | MeasurementTriggerTemplate |
    SurveyTrigger | SurveyTriggerTemplate;
}

const isTrigger = (record: DeserializedRecord): record is PatientTrigger => record.type === "trigger";

interface SurveyTrigger extends DeserializedRecord<"survey_trigger"> {
  patientTrigger: PatientTrigger;
  patientSurvey: PatientSurvey;
  survey: Survey;
}

interface SurveyTriggerTemplate extends DeserializedRecord<"patient_survey_trigger_template"> {
  survey: {
    name: string;
  };
}

interface MeasurementTrigger extends DeserializedRecord<"measurement_trigger"> {
  valueType: MeasurementKey;
  patientTrigger: PatientTrigger;
}

interface MeasurementTriggerTemplate extends DeserializedRecord<"measurement_trigger_template"> {
  valueType: MeasurementKey;
}

interface MeasurementTriggerData extends JSONAPIRecord {
  relationships: {
    patient_trigger: JSONAPISingleRelationship;
    patient_trigger_notification_receipients: JSONAPIManyRelationship;
  }
}

interface PatientTriggerNotificationGroupData extends JSONAPIRecord {
  relationships: {
    receipients: JSONAPIManyRelationship;
  }
}


interface PatientTriggerData extends JSONAPIRecord {
  relationships: {
    patient_trigger_notification_receipients: JSONAPIManyRelationship;
    subject: JSONAPISingleRelationship;
    patient_trigger_notification_groups: JSONAPIManyRelationship;
  }
}

interface SurveyTriggerData extends JSONAPIRecord {
  relationships: {
    patient_trigger: JSONAPISingleRelationship;
    patient_trigger_notification_receipients: JSONAPIManyRelationship;
    patient_survey: JSONAPISingleRelationship;
    survey: JSONAPISingleRelationship;
  }
}

interface MeasurementEcgTrigger extends DeserializedRecord<"measurement_ecg_trigger"> {
  classification: EcgClassification;
  patientTrigger: PatientTrigger;
}

interface MeasurementEcgTriggerTemplate extends DeserializedRecord<"measurement_ecg_trigger_template"> {
  classification: EcgClassification;
}

interface MeasurementEcgTriggerData extends JSONAPIRecord {
  relationships: {
    patient_trigger: JSONAPISingleRelationship;
    patient_trigger_notification_receipients: JSONAPIManyRelationship;
  }
}

const deserializerBySubjectType = (type: string) => {
  switch (type) {
    case 'survey_trigger':
      return deserializeSurveyTrigger;
    case 'measurement_trigger':
      return deserializeMeasurementTrigger;
    case 'measurement_ecg_trigger':
      return deserializeMeasurementEcgTrigger;
    case 'patient_survey_trigger_template':
      return deserializeSurveyTriggerTemplate;
    default:
      return deserializeItem;
  }
};

const deserializePatientNotificationGroup: Deserializer<PatientTriggerNotificationGroup, PatientTriggerNotificationGroupData> = (data, included) => {
  const receipients = deserializeManyRelationship<PatientTriggerNotificationGroupReceipient>(
    data.relationships.receipients,
    included,
    deserializeItem);

  return {
    ...deserializeItem(data),
    receipients
  };
};

const deserializePatientTrigger: Deserializer<PatientTrigger, PatientTriggerData > = (data, included) => {
  const subject = deserializeSingleRelationship<SubjectTriggers>(
    data.relationships.subject,
    included,
    deserializerBySubjectType(data.relationships.subject?.data?.type)
  );

  const patientTriggerNotificationGroups = deserializeManyRelationship<PatientTriggerNotificationGroup>(
    data.relationships.patient_trigger_notification_groups,
    included,
    deserializePatientNotificationGroup);

  const notificationRecipients = deserializeManyRelationship<NotificationRecipient>(
    data.relationships.patient_trigger_notification_receipients,
    included,
    deserializeNotificationRecipient);

  return {
    ...deserializeItem(data),
    notificationRecipients,
    patientTriggerNotificationGroups,
    subject
  };};

const deserializeMeasurementTrigger: Deserializer<MeasurementTrigger, MeasurementTriggerData> = (data, included) => {
  const patientTrigger = deserializeSingleRelationship<PatientTrigger>(data.relationships.patient_trigger, included, deserializePatientTrigger);

  return {
    ...deserializeItem(data),
    patientTrigger
  };
};

const deserializeMeasurementEcgTrigger: Deserializer<MeasurementEcgTrigger, MeasurementEcgTriggerData> = (data, included) => {
  const patientTrigger = deserializeSingleRelationship<PatientTrigger>(data.relationships.patient_trigger, included, deserializePatientTrigger);

  return {
    ...deserializeItem(data),
    patientTrigger
  };
};

const deserializeSurveyTrigger: Deserializer<SurveyTrigger, SurveyTriggerData> = (data, included) => {
  const patientTrigger = deserializeSingleRelationship<PatientTrigger>(data.relationships.patient_trigger, included, deserializePatientTrigger);
  const survey = deserializeSingleRelationship<Survey>(data.relationships.survey, included);
  const patientSurvey = deserializeSingleRelationship<PatientSurvey>(data.relationships.patient_survey, included);

  return {
    ...deserializeItem(data),
    patientTrigger,
    patientSurvey: patientSurvey,
    survey: survey
  };
};

const deserializeSurveyTriggerTemplate: Deserializer<SurveyTriggerTemplate, SurveyTriggerData> = (data, included) => {
  const survey = findInclude(included, "survey", data.relationships.survey.data.id);

  return {
    ...deserializeItem(data),
    survey:  {
      name: survey?.attributes.name
    }
  };
};

export {
  deserializeMeasurementEcgTrigger,
  deserializeMeasurementTrigger,
  deserializePatientTrigger,
  deserializeSurveyTrigger,
  isTrigger,
  ThresholdType,
  MeasurementTrigger,
  MeasurementEcgTrigger,
  PatientTrigger,
  SurveyTrigger,
  SubjectTriggers,
  PatientTriggerPriorityType,
  MeasurementTriggerTemplate,
  SurveyTriggerTemplate,
  MeasurementEcgTriggerTemplate
};
